import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { secureFetch } from "../utils/secureFetch";
import { trackEvent } from "../utils/analytics";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const API_BASE = process.env.REACT_APP_API_BASE;
const URL_BASE = (process.env.REACT_APP_FRONTEND_URL || window.location.origin || "").replace(/\/+$/, "");
const BASENAME = process.env.REACT_APP_BASENAME || "";

const FEE_MAP = {
  30: 0.83,
  50: 1.17
};

const AMOUNTS = [30, 50];

export default function TopUp() {
  const email = localStorage.getItem("beercard_logged_in_email");
  const [teamId, setTeamId] = useState(localStorage.getItem("beercard_teamId") || "");
  const [clubId, setClubId] = useState(localStorage.getItem("beercard_clubId") || "");

  const [selectedAmount, setSelectedAmount] = useState(30);
  const [loading, setLoading] = useState(false);

  const surcharge = FEE_MAP[selectedAmount] || 0;
  const totalCharged = useMemo(
    () => Number(selectedAmount + surcharge).toFixed(2),
    [selectedAmount, surcharge]
  );

  useEffect(() => {
  const loadUserDetails = async () => {
    try {
      const user = await secureFetch(`${API_BASE}/user`);

      if (user?.teamId) {
        localStorage.setItem("beercard_teamId", user.teamId);
      }

      if (user?.clubId) {
        localStorage.setItem("beercard_clubId", user.clubId);
      }

      if (user?.email) {
        localStorage.setItem("beercard_logged_in_email", user.email);
      }
    } catch (err) {
      console.error("Could not load user details:", err);
    }
  };

  loadUserDetails();
}, []);

  const handleTopUp = async (amount) => {
  trackEvent("topup_started", { value: amount, currency: "AUD" });
  let currentEmail = localStorage.getItem("beercard_logged_in_email");
  let currentTeamId = localStorage.getItem("beercard_teamId");
  let currentClubId = localStorage.getItem("beercard_clubId");

  if (!currentTeamId || !currentClubId) {
    try {
      const user = await secureFetch(`${API_BASE}/user`);

      currentEmail = user?.email || currentEmail;
      currentTeamId = user?.teamId || currentTeamId;
      currentClubId = user?.clubId || currentClubId;

      if (currentEmail) localStorage.setItem("beercard_logged_in_email", currentEmail);
      if (currentTeamId) localStorage.setItem("beercard_teamId", currentTeamId);
      if (currentClubId) localStorage.setItem("beercard_clubId", currentClubId);
    } catch (err) {
      console.error("Could not refresh user details:", err);
    }
  }

  if (!currentEmail) {
    trackEvent("topup_failed", { reason: "missing_email" });
    alert("Missing email. Please log in again.");
    return;
  }

  if (!currentTeamId || !currentClubId) {
    trackEvent("topup_failed", { reason: "missing_team_context" });
    alert("Your account is missing team setup details. Please ask your team admin to check your player setup.");
    return;
  }

  try {
    setLoading(true);

    const res = await secureFetch(`${API_BASE}/create-checkout-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: currentEmail,
        amount,
        success_url: `${URL_BASE}${BASENAME}/success?session_id={CHECKOUT_SESSION_ID}&amount=${amount}`,
        cancel_url: `${URL_BASE}${BASENAME}/topup`,
        teamId: currentTeamId,
        clubId: currentClubId
      })
    });

    if (!res?.sessionId) {
      console.error("❌ No session ID returned:", res);
      trackEvent("topup_failed", { reason: "missing_stripe_session", value: amount, currency: "AUD" });
      alert("We couldn't start checkout. Please try again.");
      return;
    }

    trackEvent("topup_checkout_started", { value: amount, currency: "AUD" });
    const stripe = await stripePromise;
    const { error } = await stripe.redirectToCheckout({ sessionId: res.sessionId });

    if (error) {
      console.error("Stripe redirect error:", error);
      trackEvent("topup_failed", { reason: "stripe_redirect_error", value: amount, currency: "AUD" });
      alert("Stripe failed to redirect. Please try again.");
    }
  } catch (err) {
    console.error("❌ Error starting top-up:", err);
    trackEvent("topup_failed", { reason: "checkout_session_error", value: amount, currency: "AUD" });
    alert("Something went wrong starting your top-up. Please try again.");
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-[#111827] text-white px-4 py-6">
      <div className="mx-auto w-full max-w-md rounded-2xl bg-[#1E2A38] p-5 shadow-lg border border-white/10">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-extrabold tracking-tight">Top Up Wallet</h2>
          <p className="mt-2 text-sm text-gray-300">
            Choose an amount and pay securely with Stripe.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          {AMOUNTS.map((amount) => {
            const active = selectedAmount === amount;
            return (
              <button
                key={amount}
                type="button"
                onClick={() => setSelectedAmount(amount)}
                className={`rounded-xl border px-4 py-4 text-lg font-bold transition ${
                  active
                    ? "bg-[#F5A623] text-[#1E2A38] border-[#F5A623] shadow-md"
                    : "bg-[#243447] text-white border-white/10 hover:bg-[#2a3a4d]"
                }`}
              >
                ${amount}
              </button>
            );
          })}
        </div>

        {/* Desktop/tablet style summary */}
        <div className="hidden sm:block mb-6 overflow-hidden rounded-xl border border-white/10">
          <table className="w-full border-collapse bg-[#1E2A38] text-white">
            <tbody>
              <tr className="border-b border-white/10 bg-[#243447]">
                <td className="p-3 text-left text-gray-200">Top-up amount</td>
                <td className="p-3 text-right font-semibold">${selectedAmount.toFixed(2)}</td>
              </tr>
              <tr className="border-b border-white/10 bg-[#223040]">
                <td className="p-3 text-left text-gray-200">Card fee</td>
                <td className="p-3 text-right font-semibold">${surcharge.toFixed(2)}</td>
              </tr>
              <tr className="bg-[#111827]">
                <td className="p-3 text-left text-lg font-bold">Total charged</td>
                <td className="p-3 text-right text-lg font-extrabold text-[#F5A623]">
                  ${totalCharged}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile card summary */}
        <div className="sm:hidden mb-6 space-y-3">
          <div className="rounded-xl bg-[#243447] p-4 border border-white/10">
            <div className="text-xs uppercase tracking-wide text-gray-400">Top-up amount</div>
            <div className="mt-1 text-xl font-bold">${selectedAmount.toFixed(2)}</div>
          </div>
          <div className="rounded-xl bg-[#243447] p-4 border border-white/10">
            <div className="text-xs uppercase tracking-wide text-gray-400">Card fee</div>
            <div className="mt-1 text-xl font-bold">${surcharge.toFixed(2)}</div>
          </div>
          <div className="rounded-xl bg-[#111827] p-4 border border-[#F5A623]/40">
            <div className="text-xs uppercase tracking-wide text-gray-400">Total charged</div>
            <div className="mt-1 text-2xl font-extrabold text-[#F5A623]">${totalCharged}</div>
          </div>
        </div>

        <button
          className="w-full rounded-xl bg-blue-600 px-6 py-4 text-white font-bold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => handleTopUp(selectedAmount)}
          disabled={loading}
        >
          {loading ? "Redirecting..." : `Pay $${totalCharged}`}
        </button>

        <p className="text-xs text-gray-400 mt-4 text-center">
          Includes Stripe card processing fee. Your top-up will be added to your wallet once payment is confirmed.
        </p>
      </div>
    </div>
  );
}
